<template>
  <transition name="fade-slide" appear>
    <div
      class="top-bar"
      :class="{ 'top-bar--light': lightHeader, 'top-bar--dark': !lightHeader }"
      :style="{ backgroundColor: headerColor, color: textColor }"
    >
      <q2-dropdown
        type="neutral"
        label=""
        icon="hamburger-menu"
        popover-alignment="left"
        hide-label="true"
      >
        <q2-dropdown-item
          v-if="authStore.isInternalUser()"
          value="fi-number"
          removable="false"
          @click="goToFINumber"
          >FI Search</q2-dropdown-item
        >
        <q2-dropdown-item
            v-if="isAuthenticated"
          value="events"
          removable="false"
        >
          <a href="/events">Events</a>
        </q2-dropdown-item>
        <q2-dropdown-item
            v-if="authStore.isAdmin"
          value="credentials"
          removable="false"
        >
          <a href="/credentials">Credentials</a>
        </q2-dropdown-item>
        <q2-dropdown-item
            v-if="authStore.isAdmin"
          value="modules"
          removable="false"
        >
          <a href="/modules">Modules</a>
        </q2-dropdown-item>
        <q2-dropdown-item
            v-if="authStore.isAdmin"
          value="modules"
          removable="false"
        >
          <a href="/build-images">Build Images</a>
        </q2-dropdown-item>
        <q2-dropdown-item
            v-if="authStore.isInternal"
          value="images"
          removable="false"
        >
          <a href="/build-jobs">Build Jobs</a>
        </q2-dropdown-item>
        <q2-dropdown-item
            v-if="authStore.isAdmin"
          value="multi-build"
          removable="false"
        >
          <a href="/multi-build">Multi-Build</a>
        </q2-dropdown-item>
        <q2-dropdown-item
          v-if="authStore.isInternal"
          value="help"
          removable="false"
        >
          <div @click="openHelpLink">Help</div>
        </q2-dropdown-item>
        <q2-dropdown-item separator="true"> </q2-dropdown-item>
        <q2-dropdown-item value="log-off" removable="false" @click="logOff"
          >Log Off</q2-dropdown-item
        >
      </q2-dropdown>
      <div class="title">
        Q2 Mobile App Distribution Site - <i>{{ envTitle }}</i>
      </div>
      <div class="right-section">
        <q2-dropdown
          v-if="shouldShowFiDropdown"
          type="custom"
          :label="currentFiLabel"
          popover-alignment="right"
        >
          <!-- eslint-disable-next-line vue/no-deprecated-slot-attribute -->
          <q2-btn slot="control" intent="neutral">
            <q2-icon type="bank"></q2-icon>
            <span>{{ currentFiLabel }}</span>
          </q2-btn>
          <q2-dropdown-item
            v-for="fi in displayedFis"
            :key="fi.fiNumber"
            :value="fi.fiNumber"
            removable="false"
            @click="selectFi(fi.fiNumber)"
          >
            {{ fi.fiNumber }} - {{ fi.fiName }}
          </q2-dropdown-item>
        </q2-dropdown>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { useRouter } from "vue-router";
import { useAuthStore } from "../stores/authStore";
import { computed } from "vue";
import { useFiStore } from "../stores/buildStores";
import { logger } from "../utils/logger";
import {getFiName} from "@/utils/fiUtils";

const envTitle = process.env.VUE_APP_ENVIRONMENT_TITLE || "Unknown";
const headerColor = process.env.VUE_APP_HEADER_COLOR || "#fdf8ff";

// Determine if header color is light or dark to pick readable text color
function isLightColor(hex) {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  // W3C perceived luminance formula
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
}

const lightHeader = isLightColor(headerColor);
const textColor = lightHeader ? "#404040" : "white";

const authStore = useAuthStore();
const isAuthenticated = computed(() => !!authStore.token);
const router = useRouter();
const fiStore = useFiStore();

// Determine what FIs to display in the dropdown
const displayedFis = computed(() => {
  if (authStore.isInternalUser()) {
    // For internal users, show the currently viewing FI
    const currentFi = fiStore.getFiNumber;
    return currentFi ? [{ fiNumber: currentFi, fiName: 'Viewing' }] : [];
  }
  // Filter out "internal" from the list - it should not be selectable
  return authStore.allowedFis.filter(fi => fi.fiNumber !== 'internal');
});

const shouldShowFiDropdown = computed(() => {
  // For internal users, show dropdown if they're viewing an FI
  if (authStore.isInternalUser()) {
    return !!fiStore.getFiNumber;
  }
  // For non-internal users, show dropdown if there are selectable FIs (after filtering out "internal")
  return displayedFis.value && displayedFis.value.length > 0;
});

const currentFiLabel = computed(() => {
  const currentFi = fiStore.getFiNumber;
  if (!currentFi) return 'Select FI';

  if (authStore.isInternalUser()) {
    // For internal users, just show the FI number they're viewing
    return `${currentFi} - ${getFiName(currentFi)}`;
  }

  // Find the FI name from allowedFis
  const fi = authStore.allowedFis.find(f => f.fiNumber === currentFi);
  return fi ? `${fi.fiNumber} - ${fi.fiName}` : `FI ${currentFi}`;
});

function goToFINumber() {
  logger.debug("➡️ Going to FI Number");
  fiStore.fiNumber = null;
  router.push("/fi-search");
}


function openHelpLink() {
  const helpUrl = process.env.VUE_APP_HELP_URL;
  window.open(helpUrl, "_blank");
}

async function logOff() {
  logger.debug("🚪 Logging off...");
  await authStore.logOff();
  router.push({ path: "/" });
}

async function selectFi(fiNumber) {
  logger.debug("➡️ Switching to FI:", fiNumber);

  // Don't reload if already on this FI
  if (fiStore.getFiNumber === fiNumber) {
    logger.debug("Already on FI:", fiNumber);
    return;
  }

  try {
    fiStore.setFiNumber(fiNumber);

    // Load the FI data before navigating
    await fiStore.loadFiDataFromAPI(fiNumber);

    // Force a full page reload to the new FI page
    window.location.href = `/apps/${fiNumber}`;
  } catch (err) {
    console.error("❌ Failed to load FI data:", err);
  }
}
</script>

<style scoped>
.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  font-weight: bold;
  font-size: 16px;
  border-bottom: 1px solid #d9d9d9;

  --tct-btn-neutral-box-shadow: none;
  --tct-btn-neutral-focus-box-shadow: none;
  --tct-btn-neutral-border-color: none;
  --tct-btn-neutral-border-width: 0px;
  --tct-btn-icon-border-radius: 50%;
  --tct-btn-neutral-hover-outer-ring-color: transparent;
  --tct-btn-neutral-hover-inner-ring-color: transparent;
  --tct-dropdown-item-hover-background: #f6f6f6;
}

.top-bar--light {
  --tct-btn-neutral-background: #F5EEFA;
  --tct-btn-neutral-hover-background: #ECE3F3;
  --tct-btn-neutral-active-background: #E0D2EA;
  --tct-btn-neutral-font-color: #594bab;
}

.top-bar--dark {
  --tct-btn-neutral-background: rgba(255, 255, 255, 0.15);
  --tct-btn-neutral-hover-background: rgba(255, 255, 255, 0.25);
  --tct-btn-neutral-active-background: rgba(255, 255, 255, 0.35);
  --tct-btn-neutral-font-color: white;
}

.title {
  flex-grow: 1;
  text-align: center;
  margin: 0 10px;
}

.right-section {
  min-width: 48px;
  display: flex;
  justify-content: flex-end;
}

.left-buttons,
.right-buttons {
  display: flex;
  gap: 10px;
}

.top-button {
  background-color: white;
  color: black;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.top-button:hover {
  background-color: #ddd;
}

.fade-slide-enter-active {
  transition: all 0.8s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(15px);
}
.fade-slide-enter-to {
  opacity: 1;
  transform: translateY(0);
}

a:link, a:visited, a:hover, a:active {
  text-decoration: none;
  color: inherit;
}
</style>
